import { useInput, multipleValidation } from '@libs/hooks';
import { ActionActionTypeEnum, StoreActionTypeEnum, TCommonInputOption, TextInputEnum, TInputData, TInputMethods, TUseAppHook, TWidget } from '@libs/types';
import { useCallback, useEffect, useRef, useState, ChangeEvent, FocusEvent } from 'react';

export type TConsumerInputMethods = TInputMethods & {
    setModal: TUseAppHook['setModal'];
};

const getClearValue = (val: string, widget: TWidget<TInputData, TCommonInputOption>): string => {
    const { options, id, alternativeId } = widget;
    if (val && (options?.inputType === 'number' || options?.inputType === 'decimal')) {
        return val.replace(/,/g, '.');
    }
    if (val && (id === TextInputEnum.ENTERPASSCODE || alternativeId === TextInputEnum.ENTERPASSCODE || options?.inputType === 'numericPassword')) {
        return val.replace(/\D/g, '');
    }
    return val;
};

export const useConsumerInput = (widget: TWidget<TInputData, TCommonInputOption>, methods?: TConsumerInputMethods) => {
    const { triggerActionsByType, setModal } = methods ?? {};
    const { data, options = {} } = widget;
    const onBeforeSelect = useCallback(() => setModal?.(null), [setModal]);
    const {
        value,
        isFocused,
        isFilled,
        isError,
        isHidden,
        isDisabled,
        errorMessage,
        defaultErrorMessage,
        label,
        helperText,
        ref,
        setError,
        setErrorMessage,
        handleChange: coreHandleChange,
        handleBlur,
        handleFocus,
        handleClickDropdown,
        systemMetadata,
    } = useInput(widget, methods, {
        parseValue: getClearValue,
        onBeforeSelect,
    });
    const { mandatory, regex, validations, submitLength } = data ?? {};
    const isFirstRenderRef = useRef<boolean>(true);
    const errorDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [failedValidationInternalId, setFailedValidationInternalId] = useState<string | null | undefined>(undefined);

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const nextValue = getClearValue(e?.target?.value, widget);
            if (errorDebounceRef.current) {
                clearTimeout(errorDebounceRef.current);
            }
            errorDebounceRef.current = setTimeout(() => {
                const validationResult = multipleValidation(nextValue, mandatory, regex, defaultErrorMessage, validations);
                setError(!validationResult.isValid);
                setErrorMessage(validationResult.errorMessage);
                setFailedValidationInternalId(validationResult.failedValidationInternalId ?? null);
            }, 300);
            coreHandleChange?.(e);

            const finalSubmitLength = submitLength ?? options?.maxLength;
            if (nextValue?.length === finalSubmitLength) {
                triggerActionsByType?.(ActionActionTypeEnum.onFilled)?.then();
            }
        },
        [
            coreHandleChange,
            defaultErrorMessage,
            mandatory,
            options?.maxLength,
            regex,
            setError,
            setErrorMessage,
            submitLength,
            triggerActionsByType,
            validations,
            widget,
        ],
    );

    useEffect(() => {
        if (!isFirstRenderRef.current) {
            return;
        }
        isFirstRenderRef.current = false;
        if (value !== '' && isError !== true) {
            const validationResult = multipleValidation(value, mandatory, regex, defaultErrorMessage, validations);
            queueMicrotask(() => {
                setError(!validationResult.isValid);
                setErrorMessage(validationResult.errorMessage);
                setFailedValidationInternalId(validationResult.failedValidationInternalId ?? null);
            });
        }
    }, [defaultErrorMessage, isError, mandatory, regex, validations, value, setError, setErrorMessage, setFailedValidationInternalId]);

    useEffect(() => {
        if (systemMetadata.lastValueUpdateAction === StoreActionTypeEnum.updateWidgetValueByInternalId) {
            const validationResult = multipleValidation(value, mandatory, regex, defaultErrorMessage, validations);
            queueMicrotask(() => {
                setError(!validationResult.isValid);
                setFailedValidationInternalId(validationResult.failedValidationInternalId ?? null);
            });
        }
    }, [defaultErrorMessage, mandatory, regex, validations, value, systemMetadata.lastValueUpdateAction, setError, setFailedValidationInternalId]);

    return {
        value,
        isFocused,
        isFilled,
        isError,
        isHidden,
        isDisabled,
        errorMessage,
        defaultErrorMessage,
        label,
        helperText,
        ref,
        options,
        submitLength,
        mandatory,
        regex,
        validations,
        systemMetadata,
        handleBlur,
        handleFocus,
        handleClickDropdown,
        setError,
        setErrorMessage,
        handleChange,
        failedValidationInternalId,
        setFailedValidationInternalId,
    };
};

type TInputMask = typeof import('inputmask').default;

// https://robinherbots.github.io/Inputmask/
export const useInputMask = (
    regex?: string,
    placeholder?: string,
    mask?: string,
    showMaskOnFocus = true,
    focus = false,
    alias?: string,
    inputFormat?: string,
) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const inputMaskRef = useRef<TInputMask | null>(null);

    useEffect(() => {
        const input = inputRef?.current;
        let isMounted = true;

        if (input) {
            if (focus) {
                input.focus();
            }

            import(/* webpackChunkName: "inputmask" */ 'inputmask')
                .then((module) => {
                    if (!isMounted) {
                        return;
                    }
                    const Inputmask = (module.default || module) as unknown as TInputMask;
                    inputMaskRef.current = Inputmask;

                    Inputmask({
                        mask,
                        placeholder,
                        regex,
                        showMaskOnFocus,
                        alias,
                        inputFormat,
                    }).mask(input);
                    input.setAttribute('data-mask-ready', 'true');
                })
                .catch((error) => {
                    console.error('Error loading Inputmask', error);
                });
        }
        return function cleanup() {
            isMounted = false;
            if (input) {
                if (inputMaskRef.current) {
                    inputMaskRef.current.remove(input);
                }
                input.removeAttribute('data-mask-ready');
            }
        };
    }, [placeholder, regex, mask, showMaskOnFocus, focus, alias, inputFormat]);
    return inputRef;
};
export const useSimpleInput = (value: string) => {
    const [inputValue, setInputValue] = useState(value);
    const [isFocused, setIsFocused] = useState<boolean>(!!value);
    const [isFilled, setIsFilled] = useState<boolean>(!!value);
    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        const value = e?.target?.value;
        setIsFocused(false);
        setIsFilled(!!value);
    };

    const handleFocus = () => {
        setIsFilled(true);
        setIsFocused(true);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e?.target?.value);
    };
    return {
        handleChange,
        handleBlur,
        handleFocus,
        isFilled,
        isFocused,
        inputValue,
    };
};
